import React from "react";
import "@/App.css";
import { GoogleOAuthProvider } from "@react-oauth/google";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { AuthProvider } from "./context/AuthContext";
import { LanguageProvider } from "./context/LanguageContext";
import { CountryProvider } from "./context/CountryContext";
import { AppShellProvider } from "./context/AppShellContext";
import { UnreadMessagesProvider } from "./context/UnreadMessagesContext";
import { JobNotificationsProvider } from "./context/JobNotificationsContext";
import { useHideSplashScreen } from "./hooks/useHideSplashScreen";
import { useBlockPinchZoom } from "./hooks/useBlockPinchZoom";
import Navbar from "./components/Navbar";
import MobileHeader from "./components/MobileHeader";
import Footer from "./components/Footer";
import EcosystemSection from "./components/EcosystemSection";
import AIChat from "./components/AIChat";
import BottomNav from "./components/BottomNav";
import HomePage from "./pages/HomePage";
import JobsPage from "./pages/JobsPage";
import JobDetailResolver from "./pages/JobDetailResolver";
import CompaniesPage from "./pages/CompaniesPage";
import CompanyProfilePage from "./pages/CompanyProfilePage";
import AuthPage from "./pages/AuthPage";
import ForgotPasswordPage from "./pages/ForgotPasswordPage";
import ResetPasswordPage from "./pages/ResetPasswordPage";
import VerifyEmailPage from "./pages/VerifyEmailPage";
import TermsPage from "./pages/TermsPage";
import PrivacyPage from "./pages/PrivacyPage";
import ContactPage from "./pages/ContactPage";
import { CookieConsentBanner } from "./components/CookieConsentBanner";
import { Toaster } from "./components/ui/sonner";
import { NotificationOnboardingPrompt } from "./components/NotificationOnboardingPrompt";
import { EmailVerificationPrompt } from "./components/EmailVerificationPrompt";
import UserDashboard from "./pages/UserDashboard";
import CompanyDashboard from "./pages/CompanyDashboard";
import CVBuilderPage from "./pages/CVBuilderPage";
import ProfessionalProfilePage from "./pages/ProfessionalProfilePage";
import CompanyPreviewPage from "./pages/CompanyPreviewPage";
import MessagesPage from "./pages/MessagesPage";
import { AdminAuthProvider } from "./admin/AdminAuthContext";
import AdminLoginPage from "./admin/AdminLoginPage";
import AdminForgotPasswordPage from "./admin/AdminForgotPasswordPage";
import AdminResetPasswordPage from "./admin/AdminResetPasswordPage";
import AdminChangePasswordPage from "./admin/AdminChangePasswordPage";
import AdminLayout from "./admin/AdminLayout";
import AdminDashboardPage from "./admin/AdminDashboardPage";
import AdminUsersPage from "./admin/AdminUsersPage";
import AdminCompaniesPage from "./admin/AdminCompaniesPage";
import AdminCompanyDetailPage from "./admin/AdminCompanyDetailPage";
import AdminJobsPage from "./admin/AdminJobsPage";
import AdminJobDetailPage from "./admin/AdminJobDetailPage";
import AdminReportsPage from "./admin/AdminReportsPage";
import AdminPlansPage from "./admin/AdminPlansPage";
import AdminPlanDetailPage from "./admin/AdminPlanDetailPage";
import AdminAuditPage from "./admin/AdminAuditPage";
import AdminExternalJobsPage from "./admin/AdminExternalJobsPage";
import AdminEmailDiagnosticsPage from "./admin/AdminEmailDiagnosticsPage";

function ScrollToTop() {
  const { pathname } = useLocation();
  React.useEffect(() => {
    window.scrollTo(0, 0);
    document.documentElement.scrollTop = 0;
    document.body.scrollTop = 0;
  }, [pathname]);
  return null;
}

function AppRouter() {
  const location = useLocation();
  useHideSplashScreen();
  useBlockPinchZoom();

  // /admin is a fully isolated CRM shell — no consumer chrome (Navbar/BottomNav/AI FAB/Footer).
  if (location.pathname.startsWith("/admin")) {
    return (
      <AdminAuthProvider>
        <ScrollToTop />
        <Routes>
          <Route path="/admin/login" element={<AdminLoginPage />} />
          <Route path="/admin/forgot-password" element={<AdminForgotPasswordPage />} />
          <Route path="/admin/reset-password" element={<AdminResetPasswordPage />} />
          <Route path="/admin/change-password" element={<AdminChangePasswordPage />} />
          <Route path="/admin" element={<AdminLayout />}>
            <Route index element={<AdminDashboardPage />} />
            <Route path="users" element={<AdminUsersPage />} />
            <Route path="companies" element={<AdminCompaniesPage />} />
            <Route path="companies/:companyId" element={<AdminCompanyDetailPage />} />
            <Route path="jobs" element={<AdminJobsPage />} />
            <Route path="jobs/:jobId" element={<AdminJobDetailPage />} />
            <Route path="trust-safety" element={<AdminReportsPage />} />
            <Route path="plans" element={<AdminPlansPage />} />
            <Route path="plans/new" element={<AdminPlanDetailPage />} />
            <Route path="plans/:planId" element={<AdminPlanDetailPage />} />
            <Route path="audit" element={<AdminAuditPage />} />
            <Route path="external-jobs" element={<AdminExternalJobsPage />} />
            <Route path="diagnostics/email" element={<AdminEmailDiagnosticsPage />} />
            <Route path="*" element={<Navigate to="/admin" replace />} />
          </Route>
        </Routes>
      </AdminAuthProvider>
    );
  }

  // Google OAuth is now handled fully client-side by Google Identity Services
  // (see AuthPage.jsx) — no more hash-based session_id redirect through
  // auth.emergentagent.com retired — Google OAuth is now InfoCityJobs' own
  // Google Identity Services flow (see AuthPage.jsx / POST /api/auth/google).

  // Single source of truth for the mobile App Shell (MobileHeader + BottomNav +
  // footer-hiding), applied identically to guests, candidates and employers.
  // Only /cv-builder opts out (dedicated full-screen builder). /company-view
  // ("Cómo me ven las empresas") stays INSIDE the shell so Bottom Nav/Perfil
  // active state is never lost — it's a candidate screen, not a standalone page.
  const showAppShell = location.pathname !== "/cv-builder";

  return (
    <>
      <ScrollToTop />
      <Navbar />
      {showAppShell && <MobileHeader />}
      <main className={`${showAppShell ? "pt-mobile-header pb-bottom-nav" : ""}`}>
        <Routes>
          <Route path="/" element={<HomePage />} />
          <Route path="/jobs" element={<JobsPage />} />
          <Route path="/jobs/:id" element={<JobDetailResolver />} />
          <Route path="/companies" element={<CompaniesPage />} />
          <Route path="/companies/:id" element={<CompanyProfilePage />} />
          <Route path="/auth" element={<AuthPage />} />
          <Route path="/forgot-password" element={<ForgotPasswordPage />} />
          <Route path="/reset-password" element={<ResetPasswordPage />} />
          <Route path="/verify-email" element={<VerifyEmailPage />} />
          <Route path="/terms" element={<TermsPage />} />
          <Route path="/privacy" element={<PrivacyPage />} />
          <Route path="/contact" element={<ContactPage />} />
          <Route path="/dashboard" element={<UserDashboard />} />
          <Route path="/company/dashboard" element={<CompanyDashboard />} />
          <Route path="/cv-builder" element={<CVBuilderPage />} />
          <Route path="/professionals/:id" element={<ProfessionalProfilePage />} />
          <Route path="/company-view" element={<CompanyPreviewPage />} />
          <Route path="/messages" element={<MessagesPage />} />
        </Routes>
      </main>
      {/* The long institutional web footer never renders under the mobile App
          Shell — it stays desktop-only whenever the shell is active. The
          ecosystem section is its institutional lead-in, same visibility rule. */}
      <div className={showAppShell ? "hidden lg:block" : ""}>
        <EcosystemSection />
        <Footer />
      </div>
      {showAppShell && <BottomNav />}
      <AIChat />
      <CookieConsentBanner />
      <NotificationOnboardingPrompt />
      <EmailVerificationPrompt />
    </>
  );
}

function App() {
  return (
    <GoogleOAuthProvider clientId={process.env.REACT_APP_GOOGLE_CLIENT_ID}>
      <Toaster />
      <BrowserRouter>
        <LanguageProvider>
          <CountryProvider>
            <AuthProvider>
              <UnreadMessagesProvider>
                <JobNotificationsProvider>
                  <AppShellProvider>
                    <AppRouter />
                  </AppShellProvider>
                </JobNotificationsProvider>
              </UnreadMessagesProvider>
            </AuthProvider>
          </CountryProvider>
        </LanguageProvider>
      </BrowserRouter>
    </GoogleOAuthProvider>
  );
}

export default App;
